import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Ticket, ChevronDown, ChevronUp, Radio } from "lucide-react";
import BoxScore from "../components/BoxScore";
import GameAlertSignup from "../components/GameAlertSignup";
import StructuredData from "../components/StructuredData";

export default function Schedule() {
    const [games, setGames] = useState([]);
    const [tab, setTab] = useState("upcoming");
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { api.get("/games").then((r) => setGames(r.data)); }, []);

    const upcoming = games.filter((g) => g.status !== "final");
    const results = games.filter((g) => g.status === "final");
    const items = tab === "upcoming" ? upcoming : results;

    const eventSchema = upcoming.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": upcoming.slice(0, 20).map((g, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
                "@type": "SportsEvent",
                "name": `${g.home_away === "home" ? "Purple Kings vs." : "Purple Kings @"} ${g.opponent}`,
                "startDate": g.date,
                "eventStatus": "https://schema.org/EventScheduled",
                "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
                "location": {
                    "@type": "Place",
                    "name": g.home_away === "home" ? "Stellar Bank Gym at Fulshear High School" : (g.location || "Away"),
                    "address": g.home_away === "home"
                        ? { "@type": "PostalAddress", "addressLocality": "Fulshear", "addressRegion": "TX", "addressCountry": "US" }
                        : { "@type": "PostalAddress", "addressLocality": g.location || "TX", "addressCountry": "US" },
                },
                "organizer": { "@type": "SportsTeam", "name": "Purple Kings", "url": "https://purplekingshoops.com/" },
                "url": "https://purplekingshoops.com/schedule",
            },
        })),
    } : null;

    return (
        <div data-testid="schedule-page" className="grain">
            <StructuredData data={eventSchema} />
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">2026-2027 Season</div>
                    <h1 className="h-display text-6xl md:text-8xl">Upcoming Games</h1>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
                <div className="flex gap-2 mb-8">
                    <button
                        onClick={() => setTab("upcoming")}
                        data-testid="schedule-tab-upcoming"
                        className={`px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold border ${tab === "upcoming" ? "bg-gold text-black border-gold" : "border-line text-white hover:border-gold hover:text-gold"}`}
                    >
                        Upcoming ({upcoming.length})
                    </button>
                    <button
                        onClick={() => setTab("results")}
                        data-testid="schedule-tab-results"
                        className={`px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold border ${tab === "results" ? "bg-gold text-black border-gold" : "border-line text-white hover:border-gold hover:text-gold"}`}
                    >
                        Results ({results.length})
                    </button>
                </div>

                <div className="space-y-3">
                    {items.map((g, i) => (
                        <div key={g.id} className="card-flat" data-testid={`game-row-${i}`}>
                            <div className="p-5 md:p-6 grid grid-cols-[80px_1fr_auto] gap-5 items-center">
                                <div>
                                    <div className="h-display text-4xl text-gold leading-none">
                                        {new Date(g.date).toLocaleDateString("en-US", { day: "2-digit", timeZone: "America/Chicago" })}
                                    </div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-ink mt-1">
                                        {new Date(g.date).toLocaleDateString("en-US", { month: "short", timeZone: "America/Chicago" })}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-ink">
                                        {g.home_away === "home" ? "Home" : "Away"} · {g.location}
                                    </div>
                                    <div className="h-display text-xl md:text-2xl mt-1">
                                        Purple Kings <span className="text-ink mx-2">vs.</span> {g.opponent}
                                    </div>
                                    {g.notes && g.notes.includes("Broadcast:") && (
                                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-gold/10 border border-gold/40 text-gold text-[10px] tracking-[0.25em] uppercase font-bold" data-testid={`game-broadcast-${i}`}>
                                            <Radio size={11} /> {g.notes.split("Broadcast:")[1].trim()}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                    {g.status === "final" ? (
                                        <>
                                            <div className={`h-display text-2xl ${g.result?.startsWith("W") ? "text-green-400" : "text-red-400"}`}>{g.result}</div>
                                            <button
                                                onClick={() => setExpanded(expanded === g.id ? null : g.id)}
                                                data-testid={`game-box-toggle-${i}`}
                                                className="text-gold text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 hover:text-gold-bright"
                                            >
                                                Box Score {expanded === g.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <div className="h-display text-xl text-gold">
                                                    {(g.notes || "").includes("Time TBA")
                                                        ? "TBA"
                                                        : new Date(g.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })}
                                                </div>
                                                <div className="text-[10px] uppercase text-ink tracking-widest">
                                                    {g.notes?.toLowerCase().includes("exhibition") ? "Exhibition" : "CT"}
                                                </div>
                                            </div>
                                            {g.home_away === "home" && (
                                                <Link
                                                    to={`/tickets?game=${g.id}`}
                                                    data-testid={`game-buy-${i}`}
                                                    className="btn-gold px-4 py-2 text-[10px] inline-flex items-center gap-1.5"
                                                >
                                                    <Ticket size={12} /> Buy Tickets
                                                </Link>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            {expanded === g.id && (
                                <div className="border-t border-line p-2 md:p-4" data-testid={`game-box-${i}`}>
                                    <BoxScore gameId={g.id} />
                                </div>
                            )}
                        </div>
                    ))}
                    {items.length === 0 && <div className="text-center text-ink py-20">Nothing here yet.</div>}
                </div>
            </section>

            <GameAlertSignup source="schedule" />
        </div>
    );
}
